import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'
import type { 
  CreateRolePayload, 
  UpdateRolePayload,
  CreatePermissionPayload,
  UpdatePermissionPayload
} from '../types'
import { normalizePhone } from '../lib/phone'
import {
  clearSession,
  getStoredToken,
  notifySessionEnded,
  refreshAccessToken,
} from './authSession'
import { apiBaseUrl } from './config'

/**
 * Client HTTP partagé de l'administration.
 *
 * Il est exporté pour que la couche temps réel (`services/echo`) réutilise
 * exactement les mêmes intercepteurs : un 401 sur `/broadcasting/auth` doit
 * déclencher la rotation du jeton Sanctum puis le rejeu de la requête, au lieu
 * d'abandonner la souscription Pusher hors de tout intercepteur.
 */
export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

/**
 * Endpoints d'authentification : ils ne doivent JAMAIS déclencher la rotation
 * automatique (un 401 sur /auth/refresh relancerait un refresh à l'infini) ni
 * la fermeture forcée de session — le store d'authentification gère ces flux.
 */
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/auth/logout-all',
  '/logout',
  '/logout-all',
]

/** Requête dont la reprise après rotation a déjà été tentée (une seule fois). */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false

  return AUTH_ENDPOINTS.some((endpoint) => url === endpoint || url.startsWith(`${endpoint}?`))
}

// Injection du jeton — source de vérité : services/authSession.
api.interceptors.request.use((config) => {
  const token = getStoredToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Rotation de jeton et fin de session.
//
// Le backend révoque un jeton dès qu'il est échangé (POST /auth/refresh) ou dès
// que le compte est suspendu : un 401 signifie donc « jeton expiré ou révoqué ».
// On tente alors une rotation unique (l'ancien jeton est remplacé par un neuf à
// expiration glissante), puis on rejoue la requête initiale à l'identique. Si
// la rotation échoue, la session est définitivement close : le store vide son
// état et l'utilisateur revient à l'écran de connexion.
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status: number | undefined = error.response?.status
    const config = error.config as RetriableConfig | undefined
    const message: string | undefined = error.response?.data?.message

    // Compte désactivé/suspendu : le backend refuse tout jeton dont le porteur
    // n'est plus actif — la session est close immédiatement.
    if (status === 403 && typeof message === 'string' && /désactivé|suspendu/i.test(message)) {
      notifySessionEnded('revoked')

      return Promise.reject(error)
    }

    if (status !== 401 || !config) {
      return Promise.reject(error)
    }

    // Aucun jeton stocké : session déjà close (ou jamais ouverte), rien à tenter.
    if (!getStoredToken()) {
      clearSession()

      return Promise.reject(error)
    }

    // Flux d'authentification, ou rotation déjà tentée pour cette requête :
    // on ne boucle pas, le store décide de la suite (login, déconnexion…).
    if (isAuthEndpoint(config.url) || config._retried) {
      return Promise.reject(error)
    }

    config._retried = true

    try {
      const token = await refreshAccessToken()
      config.headers.Authorization = `Bearer ${token}`

      return api.request(config)
    } catch {
      notifySessionEnded('expired')

      return Promise.reject(error)
    }
  }
)

// ─── API Services ─────────────────────────────────────────────────
export const dashboardApi = {
  getStats:      () => api.get('/admin/finance/overview'),
  getRideStats:  () => api.get('/admin/rides/stats'),
  getUserStats:  () => api.get('/admin/users/stats'),
  getOverview:   (params?: object) => api.get('/admin/reports', { params }),
  getRides:      (params?: object) => api.get('/admin/rides', { params }),
  getReports:    (type: string, params?: object) => api.get(`/admin/reports/${type}`, { params }),
}

export const ridesApi = {
  list:        (params?: object) => api.get('/admin/rides', { params }),
  show:        (id: string | number) => api.get(`/admin/rides/${id}`),
  stats:       () => api.get('/admin/rides/stats'),
  forceCancel: (id: string | number, reason: string) => api.patch(`/admin/rides/${id}/cancel`, { reason }),
}

export const usersApi = {
  list:     (params?: object) => api.get('/admin/users', { params }),
  create:   (data: object) => api.post('/admin/users', data),
  // Création d'un chauffeur : compte (rôle driver) + profil véhicule, en une seule
  // opération atomique côté backend (2 en 1 : pas de compte orphelin possible).
  createDriver: (data: object) => api.post('/admin/users/drivers', data),
  show:     (id: string | number) => api.get(`/admin/users/${id}`),
  update:   (id: string | number, data: object) => api.patch(`/admin/users/${id}`, data),
  stats:    () => api.get('/admin/users/stats'),
  suspend:  (id: string | number, reason?: string) => api.patch(`/admin/users/${id}/suspend`, { reason }),
  activate: (id: string | number) => api.patch(`/admin/users/${id}/activate`),
  // Catalogue des rôles staff/admin (catégories + noms) fourni par le backend.
  staffRoles: () => api.get('/admin/users/staff-roles'),
}

// ─── Dossier chauffeur (panel admin) ─────────────────────────────
export const driversApi = {
  // Fiche complète : compte, véhicule, localisation, performance, finances,
  // abonnement, courses et avis récents.
  detail: (id: string | number) => api.get(`/admin/drivers/${id}`),
  // Vérification du profil véhicule (policy : admin / superadmin).
  verifyProfile: (profileId: string | number) => api.post(`/driver/profiles/${profileId}/verify`),
  unverifyProfile: (profileId: string | number) => api.post(`/driver/profiles/${profileId}/unverify`),
}

export interface ProcessPayoutPayload {
  driver_id: number
  amount: number
  method: 'mobile_money' | 'bank_transfer'
  phone_number?: string
  bank_name?: string
  bank_account?: string
}

export const financeApi = {
  overview:      () => api.get('/admin/finance/overview'),
  transactions:  (params?: object) => api.get('/admin/finance/transactions', { params }),
  payouts:       (params?: object) => api.get('/admin/finance/payouts', { params }),
  payoutHistory: (params?: object) => api.get('/admin/finance/payouts/history', { params }),
  payoutStats:   (params?: object) => api.get('/admin/finance/payouts/stats', { params }),
  payoutReport:  (params?: object) => api.get('/admin/finance/report', { params }),
  processPayout: (data: ProcessPayoutPayload) => api.post('/admin/finance/payouts', data),
  approvePayout: (id: string | number) => api.patch(`/admin/finance/payouts/${id}/approve`),
  cancelPayout:  (id: string | number, data?: object) => api.patch(`/admin/finance/payouts/${id}/cancel`, data ?? {}),
  exportPayouts: (params?: object) => api.get('/admin/finance/payouts/export', { params, responseType: 'blob' }),
}

export const promoApi = {
  list:   (params?: object) => api.get('/admin/promo-codes', { params }),
  show:   (id: string | number) => api.get(`/admin/promo-codes/${id}`),
  create: (data: object) => api.post('/admin/promo-codes', data),
  update: (id: string | number, data: object) => api.patch(`/admin/promo-codes/${id}`, data),
  toggle: (id: string | number) => api.patch(`/admin/promo-codes/${id}/toggle`),
  delete: (id: string | number) => api.delete(`/admin/promo-codes/${id}`),
}

export const tariffApi = {
  list:   () => api.get('/admin/tarifs'),
  create: (data: object) => api.post('/admin/tarifs', data),
  update: (id: string | number, data: object) => api.patch(`/admin/tarifs/${id}`, data),
  toggle: (id: string | number) => api.patch(`/admin/tarifs/${id}/toggle`),
  delete: (id: string | number) => api.delete(`/admin/tarifs/${id}`),
}

export const claimsApi = {
  list:    (params?: object) => api.get('/admin/claims', { params }),
  show:    (id: string | number) => api.get(`/admin/claims/${id}`),
  stats:   () => api.get('/admin/claims/stats'),
  update:  (id: string | number, data: object) => api.patch(`/admin/claims/${id}`, data),
  resolve: (id: string | number, data: { status: 'resolved' | 'rejected'; resolution?: string; admin_notes?: string; refund_amount?: number }) =>
    api.patch(`/admin/claims/${id}/resolve`, data),
  assign:  (id: string | number, adminId: number) => api.patch(`/admin/claims/${id}/assign`, { admin_id: adminId }),
  addNote: (id: string | number, note: string) => api.post(`/admin/claims/${id}/notes`, { note }),
  notify:  (id: string | number, data: object) => api.post(`/admin/claims/${id}/notify`, data),
}

export const reportApi = {
  overview: (params?: object) => api.get('/admin/reports', { params }),
  rides:   (params?: object) => api.get('/admin/reports/rides', { params }),
  revenue: (params?: object) => api.get('/admin/reports/revenue', { params }),
  drivers: (params?: object) => api.get('/admin/reports/drivers', { params }),
  export:  (type: 'overview' | 'rides' | 'revenue' | 'drivers' | 'financial', format: 'csv' | 'excel' | 'xlsx', params?: object) =>
    api.get('/admin/reports/export', { params: { ...params, type, format }, responseType: 'blob' }),
}

// ─── Rôles & Permissions API ──────────────────────────────────────
export const rolesApi = {
  list:            (params?: object) => api.get('/super-admin/roles', { params }),
  show:            (id: number) => api.get(`/super-admin/roles/${id}`),
  create:          (data: CreateRolePayload) => api.post('/super-admin/roles', data),
  update:          (id: number, data: UpdateRolePayload) => api.patch(`/super-admin/roles/${id}`, data),
  delete:          (id: number) => api.delete(`/super-admin/roles/${id}`),
  syncPermissions: (id: number, permissions: string[]) => api.patch(`/super-admin/roles/${id}/permissions`, { permissions }),
}

export const permissionsApi = {
  list:    (params?: object) => api.get('/super-admin/permissions', { params }),
  grouped: () => api.get('/super-admin/permissions'),
  groups:  () => api.get('/super-admin/permissions/groups'),
  show:    (id: number) => api.get(`/super-admin/permissions/${id}`),
  create:  (data: CreatePermissionPayload) => api.post('/super-admin/permissions', data),
  update:  (id: number, data: UpdatePermissionPayload) => api.patch(`/super-admin/permissions/${id}`, data),
  delete:  (id: number) => api.delete(`/super-admin/permissions/${id}`),
}

export const authApi = {
  // Ouverture de session. Le numéro est normalisé vers la forme canonique
  // « 226XXXXXXXX » (miroir du backend App\Support\Phone) : « +226 70 12 34
  // 56 », « 00226… » et « 70 12 34 56 » atteignent tous le même compte.
  // Le backend renvoie `token` + `expires_at` (SANCTUM_TOKEN_EXPIRATION_DAYS).
  login:         (phone: string, password: string) => api.post('/auth/login', { phone: normalizePhone(phone), password }),
  // Rotation de jeton : l'ancien est révoqué, le nouveau a une expiration glissante.
  refresh:       () => api.post('/auth/refresh'),
  // Déconnexion locale : révoque uniquement le jeton courant.
  logout:        () => api.post('/logout'),
  // Déconnexion de toutes les sessions : révoque tous les jetons de l'utilisateur.
  logoutAll:     () => api.post('/logout-all'),
  myPermissions: () => api.get('/me/permissions'),
  profile:       () => api.get('/me'),
  updateProfile: (data: object) => api.patch('/me', data),
}

// ─── Zones API ──────────────────────────────────────────────────
export const zonesApi = {
  list:    (params?: object) => api.get('/admin/zones', { params }),
  show:    (id: string | number) => api.get(`/admin/zones/${id}`),
  create:  (data: object) => api.post('/admin/zones', data),
  update:  (id: string | number, data: object) => api.patch(`/admin/zones/${id}`, data),
  delete:  (id: string | number) => api.delete(`/admin/zones/${id}`),
  toggle:  (id: string | number) => api.patch(`/admin/zones/${id}/toggle`),
}

// ─── Flash infos API (bandeaux des espaces client & chauffeur) ──
// Permissions backend : flash_infos.view / create / edit / delete.
export const flashInfosApi = {
  list:   (params?: object) => api.get('/admin/flash-infos', { params }),
  show:   (id: string | number) => api.get(`/admin/flash-infos/${id}`),
  create: (data: object) => api.post('/admin/flash-infos', data),
  update: (id: string | number, data: object) => api.patch(`/admin/flash-infos/${id}`, data),
  toggle: (id: string | number) => api.patch(`/admin/flash-infos/${id}/toggle`),
  delete: (id: string | number) => api.delete(`/admin/flash-infos/${id}`),
}

// ─── Types de véhicules API (catalogue dynamique) ───────────────
// Source de vérité des slugs utilisés par les cours, les tarifs et les profils
// chauffeurs. Permissions backend : vehicle_types.view / create / edit / delete.
// NB : le slug est définitif (clé de jointure) — il n'est jamais mis à jour.
export const vehicleTypesApi = {
  list:   (params?: object) => api.get('/admin/vehicle-types', { params }),
  show:   (id: string | number) => api.get(`/admin/vehicle-types/${id}`),
  create: (data: object) => api.post('/admin/vehicle-types', data),
  update: (id: string | number, data: object) => api.patch(`/admin/vehicle-types/${id}`, data),
  toggle: (id: string | number) => api.patch(`/admin/vehicle-types/${id}/toggle`),
  delete: (id: string | number) => api.delete(`/admin/vehicle-types/${id}`),
}

// ─── Notifications API (panel admin) ─────────────────────────────
export const notificationApi = {
  list:         (params?: object) => api.get('/admin/notifications', { params }),
  send:         (data: object) => api.post('/admin/notifications/send', data),
  broadcast:    (data: object) => api.post('/admin/notifications/broadcast', data),
  markAsRead:   (id: string | number) => api.patch(`/admin/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/admin/notifications/read-all'),
  unreadCount:  () => api.get('/notifications/unread-count'),
}

// ─── Subscriptions API ────────────────────────────────────────
export const subscriptionsApi = {
  // Plans
  plans:       () => api.get('/admin/subscription-plans'),
  showPlan:    (id: string | number) => api.get(`/admin/subscription-plans/${id}`),
  createPlan:  (data: object) => api.post('/admin/subscription-plans', data),
  updatePlan:  (id: string | number, data: object) => api.patch(`/admin/subscription-plans/${id}`, data),
  deletePlan:  (id: string | number) => api.delete(`/admin/subscription-plans/${id}`),
  togglePlan:  (id: string | number) => api.patch(`/admin/subscription-plans/${id}/toggle`),
  // Subscriptions
  list:        (params?: object) => api.get('/admin/subscriptions', { params }),
  show:        (id: string | number) => api.get(`/admin/subscriptions/${id}`),
  cancel:      (id: string | number, data?: object) => api.patch(`/admin/subscriptions/${id}/cancel`, data ?? {}),
  extend:      (id: string | number, data: object) => api.patch(`/admin/subscriptions/${id}/extend`, data),
  stats:       () => api.get('/admin/subscriptions/stats'),
}

// ─── Settings API ─────────────────────────────────────────────
export const settingsApi = {
  get:    () => api.get('/admin/settings'),
  update: (data: object) => api.patch('/admin/settings', data),
}

// ─── Audit Logs API ───────────────────────────────────────────
export const auditLogsApi = {
  list:   (params?: object) => api.get('/admin/audit-logs', { params }),
  show:   (id: string | number) => api.get(`/admin/audit-logs/${id}`),
  stats:  (params?: object) => api.get('/admin/audit-logs/stats', { params }),
  export: (params?: object) => api.get('/admin/audit-logs/export', { params, responseType: 'blob' }),
}

// Alias de compatibilité (mêmes endpoints que notificationApi)
export const notificationsApi = notificationApi

export default api
